import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { createPortal } from "react-dom";
import $ from "jquery";
import ApiURl from "../../controllers/Api";
import { FaChartBar } from "react-icons/fa";
import {
  Bell,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  Crown,
  Diamond,
  Gift,
  Package,
  RefreshCw,
  Search,
  ShoppingBag,
  Sparkles,
  TrendingUp,
  UserRound,
  UsersRound,
  WalletCards,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// Toast Component
function ToastStack({ toasts, onRemove }) {
  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
                ? "border-red-200 bg-red-50 text-red-900"
                : "border-[#b29063]/20 bg-[#b29063]/10 text-[#b29063]/90";

          return (
            <div
              key={t.id}
              className={[
                "pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border bg-white/60 px-2 py-1 text-xs font-semibold hover:bg-white"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Stock Update Modal
const StockUpdateModal = React.memo(({ isOpen, onClose, product, onUpdate }) => {
  const [stockValue, setStockValue] = useState("");
  const [updating, setUpdating] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && product) {
      setStockValue(product.stock_quantity?.toString() || "0");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newStock = parseInt(stockValue);
    if (isNaN(newStock) || newStock < 0) {
      alert("Please enter a valid stock quantity");
      return;
    }

    setUpdating(true);

    try {
      const formData = new FormData();
      formData.append("product_id", product.product_id);
      formData.append("stock_quantity", newStock);

      const response = await $.ajax({
        url: `${ApiURl}/updateProductimg.php`,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        // INSTANT UPDATE - call onUpdate immediately
        onUpdate(product.product_id, newStock);
        onClose();
      } else {
        alert(response?.message || "Failed to update stock");
      }
    } catch (error) {
      console.error("Stock update error:", error);
      alert("Error updating stock");
    } finally {
      setUpdating(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Update Stock - {product.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Stock: <span className="font-bold">{product.stock_quantity}</span>
            </label>
            <input
              ref={inputRef}
              type="number"
              min="0"
              value={stockValue}
              onChange={(e) => setStockValue(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#b29063] focus:border-transparent outline-none"
              placeholder="Enter new stock quantity"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              className="flex-1 px-4 py-3 bg-[#b29063] text-white rounded-xl font-semibold hover:bg-[#9e7e53] disabled:opacity-50"
            >
              {updating ? "Updating..." : "Update Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
});

function MiniBarChart({ title, data, valueKey, labelKey = "label", valueFormatter }) {
  const maxValue = Math.max(...data.map((item) => Number(item[valueKey] || 0)), 1);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-gray-800">{title}</h3>
      <div className="mt-5 space-y-3">
        {data.length ? (
          data.map((item) => {
            const value = Number(item[valueKey] || 0);
            const width = `${Math.max(6, (value / maxValue) * 100)}%`;

            return (
              <div key={item[labelKey]} className="grid grid-cols-[120px_1fr_auto] items-center gap-3">
                <span className="truncate text-xs font-medium text-gray-600">{item[labelKey]}</span>
                <div className="h-3 overflow-hidden rounded-full bg-[#b29063]/10">
                  <div className="h-full rounded-full bg-[#b29063]" style={{ width }} />
                </div>
                <span className="text-xs font-semibold text-gray-900">
                  {valueFormatter ? valueFormatter(value) : value}
                </span>
              </div>
            );
          })
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">No data available yet.</p>
        )}
      </div>
    </div>
  );
}

function DonutChart({ title, data }) {
  const total = data.reduce((sum, item) => sum + Number(item.count || 0), 0);
  let currentOffset = 25;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-gray-800">{title}</h3>
      <div className="mt-5 flex flex-col items-center gap-5 sm:flex-row">
        <div className="relative h-40 w-40 shrink-0">
          <svg viewBox="0 0 42 42" className="h-full w-full -rotate-90">
            <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#f1ece4" strokeWidth="7" />
            {total > 0 &&
              data.map((item) => {
                const count = Number(item.count || 0);
                const dash = (count / total) * 100;
                const circle = (
                  <circle
                    key={item.status}
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke={item.color}
                    strokeWidth="7"
                    strokeDasharray={`${dash} ${100 - dash}`}
                    strokeDashoffset={currentOffset}
                  />
                );
                currentOffset -= dash;
                return circle;
              })}
          </svg>
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{total}</p>
              <p className="text-[10px] uppercase tracking-[0.12em] text-gray-500">Orders</p>
            </div>
          </div>
        </div>

        <div className="w-full space-y-3">
          {data.map((item) => (
            <div key={item.status} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-medium capitalize text-gray-700">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                {item.status}
              </span>
              <span className="text-sm font-bold text-gray-900">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  // Toast state
  const [toasts, setToasts] = useState([]);
  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((p) => [...p, { id, type, message, title }]);
    window.setTimeout(() => {
      setToasts((p) => p.filter((x) => x.id !== id));
    }, 3000);
  };
  const removeToast = (id) => setToasts((p) => p.filter((x) => x.id !== id));

  // Sales Statistics
  const [totalSales, setTotalSales] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [monthSales, setMonthSales] = useState(0);
  const [newOrders, setNewOrders] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [completedOrders, setCompletedOrders] = useState(0);
  const [cancelledOrders, setCancelledOrders] = useState(0);

  // Stock Statistics
  const [totalProducts, setTotalProducts] = useState(0);
  const [outOfStock, setOutOfStock] = useState(0);
  const [lowStock, setLowStock] = useState(0);
  const [criticalStock, setCriticalStock] = useState(0);
  const [healthyStock, setHealthyStock] = useState(0);
  const [averageStock, setAverageStock] = useState(0);

  // All Products Data (for filtering)
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Filtered Products for Display
  const [displayProducts, setDisplayProducts] = useState([]);

  // Stock Update Modal State
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Filter States
  const [filterType, setFilterType] = useState("lowest"); // lowest, critical, low, out, all
  const [filterLimit, setFilterLimit] = useState(10); // 10, 20, 50, 100
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("stock_asc"); // stock_asc, stock_desc, name_asc, name_desc
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  // Pagination for "all" view
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const [recentOrders, setRecentOrders] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [orderStatusDistribution, setOrderStatusDistribution] = useState([]);
  const [categoryStockDistribution, setCategoryStockDistribution] = useState([]);
  const [revenueByCategory, setRevenueByCategory] = useState([]);
  const [bestSellingProducts, setBestSellingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const role = localStorage.getItem("role");
    if (role !== "admin") navigate("/unauthorized");
  }, [navigate]);

  // Fetch all products for filtering
  const fetchAllProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${ApiURl}/getProducts.php`);
      const data = await response.json();
      
      if (data?.success) {
        const products = data?.products || [];
        setAllProducts(products);
        
        // Extract unique categories for filter
        const uniqueCats = [...new Set(products.map(p => p.category_name).filter(Boolean))];
        setCategories(uniqueCats);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${ApiURl}/getDashboardData.php`);
      const data = await response.json();

      if (data?.success) {
        const d = data?.data || {};
        
        // Sales Statistics
        setTotalSales(d.total_sales || 0);
        setTodaySales(d.today_sales || 0);
        setMonthSales(d.month_sales || 0);
        setNewOrders(d.new_orders || 0);
        setTotalOrders(d.total_orders || 0);
        setPendingOrders(d.pending_orders || 0);
        setCompletedOrders(d.completed_orders || 0);
        setCancelledOrders(d.cancelled_orders || 0);

        // Stock Statistics
        setTotalProducts(d.total_products || 0);
        setOutOfStock(d.out_of_stock || 0);
        setLowStock(d.low_stock || 0);
        setCriticalStock(d.critical_stock || 0);
        setHealthyStock(d.healthy_stock || 0);
        setAverageStock(d.average_stock || 0);

        setRecentOrders(d.recent_orders || []);
        setSalesTrend(d.sales_trend || []);
        setOrderStatusDistribution(d.order_status_distribution || []);
        setCategoryStockDistribution(d.category_stock_distribution || []);
        setRevenueByCategory(d.revenue_by_category || []);
        setBestSellingProducts(d.best_selling_products || []);

        if (Array.isArray(d.all_products)) {
          setAllProducts(d.all_products);
          setCategories([...new Set(d.all_products.map((p) => p.category_name).filter(Boolean))]);
        }

        setError(null);
        setLoading(false);
      } else {
        setError(data?.message || "Failed to fetch dashboard data.");
        setLoading(false);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError("An error occurred while fetching data. Please try again.");
      setLoading(false);
    }
  };

  // Fetch all products on mount
  useEffect(() => {
    fetchDashboardData();
    fetchAllProducts();
    const intervalId = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Apply filters to products - REAL-TIME FILTERING
  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.product_id?.toString().includes(searchTerm)
      );
    }

    // Apply category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(p => p.category_name === categoryFilter);
    }

    // Apply stock status filter
    switch (filterType) {
      case "critical":
        filtered = filtered.filter(p => p.stock_quantity > 0 && p.stock_quantity < 5);
        break;
      case "low":
        filtered = filtered.filter(p => p.stock_quantity > 0 && p.stock_quantity < 10);
        break;
      case "out":
        filtered = filtered.filter(p => p.stock_quantity <= 0);
        break;
      case "healthy":
        filtered = filtered.filter(p => p.stock_quantity >= 20);
        break;
      case "all":
        // Show all products
        break;
      case "lowest":
      default:
        // Show lowest stock first, then apply limit
        filtered = filtered.filter(p => p.stock_quantity > 0);
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case "stock_asc":
        filtered.sort((a, b) => (a.stock_quantity || 0) - (b.stock_quantity || 0));
        break;
      case "stock_desc":
        filtered.sort((a, b) => (b.stock_quantity || 0) - (a.stock_quantity || 0));
        break;
      case "name_asc":
        filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "name_desc":
        filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      default:
        break;
    }

    // Apply limit for non-"all" views
    if (filterType !== "all" && filterLimit > 0) {
      filtered = filtered.slice(0, filterLimit);
    }

    return filtered;
  }, [allProducts, searchTerm, categoryFilter, filterType, filterLimit, sortBy]);

  // Pagination for "all" view
  const paginatedProducts = useMemo(() => {
    if (filterType !== "all") return filteredProducts;
    
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, filterType, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // INSTANT stock update handler
  const handleStockUpdate = (productId, newStock) => {
    // Update in allProducts
    setAllProducts(prev => 
      prev.map(p => 
        p.product_id === productId 
          ? { 
              ...p, 
              stock_quantity: newStock,
              stock_status: getStockStatus(newStock),
              stock_message: getStockMessage(newStock)
            }
          : p
      )
    );

    // Update stock statistics
    updateStockStatistics(productId, newStock);

    toast("success", `Stock updated to ${newStock} units`, "Stock Updated");
  };

  const getStockStatus = (stock) => {
    if (stock <= 0) return 'out';
    if (stock < 5) return 'critical';
    if (stock < 10) return 'warning';
    return 'good';
  };

  const getStockMessage = (stock) => {
    if (stock <= 0) return 'Out of Stock';
    if (stock < 5) return `Only ${stock} left!`;
    if (stock < 10) return 'Few left!';
    return 'In Stock';
  };

  const updateStockStatistics = (productId, newStock) => {
    // This is a simplified version - you might want to track the old value
    // to accurately update statistics
    setOutOfStock(prev => {
      if (newStock <= 0) return prev + 1;
      // Check if product was out of stock before
      const wasOutOfStock = allProducts.find(p => p.product_id === productId)?.stock_quantity <= 0;
      return wasOutOfStock ? Math.max(0, prev - 1) : prev;
    });

    setLowStock(prev => {
      if (newStock > 0 && newStock < 10) return prev + 1;
      // Check if product was low stock before
      const wasLowStock = allProducts.find(p => p.product_id === productId)?.stock_quantity > 0 && 
                         allProducts.find(p => p.product_id === productId)?.stock_quantity < 10;
      return wasLowStock ? Math.max(0, prev - 1) : prev;
    });

    setCriticalStock(prev => {
      if (newStock > 0 && newStock < 5) return prev + 1;
      // Check if product was critical before
      const wasCritical = allProducts.find(p => p.product_id === productId)?.stock_quantity > 0 && 
                         allProducts.find(p => p.product_id === productId)?.stock_quantity < 5;
      return wasCritical ? Math.max(0, prev - 1) : prev;
    });

    setHealthyStock(prev => {
      if (newStock >= 20) return prev + 1;
      // Check if product was healthy before
      const wasHealthy = allProducts.find(p => p.product_id === productId)?.stock_quantity >= 20;
      return wasHealthy ? Math.max(0, prev - 1) : prev;
    });
  };

  const openStockModal = (product) => {
    setSelectedProduct(product);
    setIsStockModalOpen(true);
  };

  const resetFilters = () => {
    setFilterType("lowest");
    setFilterLimit(10);
    setSearchTerm("");
    setCategoryFilter("all");
    setSortBy("stock_asc");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!pageRef.current) return;

    gsap.fromTo(
      pageRef.current,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" }
    );

    gsap.fromTo(
      cardsRef.current,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.08 }
    );
  }, [loading]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed" || s === "delivered") {
      return "bg-green-100 text-green-800 border-green-200";
    } else if (s === "pending") {
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (s === "cancelled") {
      return "bg-red-100 text-red-800 border-red-200";
    } else {
      return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getProductImageUrl = (imgPath) => {
    const raw = String(imgPath || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const cleaned = raw
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .replace(/^["']|["']$/g, "")
      .split(/[|,]/)
      .map((item) => item.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean)[0];

    return cleaned ? `${ApiURl}/images/${encodeURIComponent(cleaned)}` : "";
  };

  const cardShell = "rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-300";


  const adminName =
    localStorage.getItem("user_name") ||
    localStorage.getItem("name") ||
    "Admin";

  const maxTrendValue = Math.max(
    ...salesTrend.map((item) => Number(item.revenue || 0)),
    1
  );

  const chartPoints = salesTrend.map((item, index) => {
    const x =
      salesTrend.length <= 1 ? 50 : (index / (salesTrend.length - 1)) * 100;
    const y = 86 - (Number(item.revenue || 0) / maxTrendValue) * 66;
    return { x, y, item };
  });

  const chartPolyline = chartPoints
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  const dashboardStats = [
    {
      label: "Total Revenue",
      value: formatCurrency(totalSales),
      note: `${formatCurrency(todaySales)} today`,
      icon: CircleDollarSign,
      tone: "maroon",
    },
    {
      label: "Orders",
      value: totalOrders.toLocaleString("en-IN"),
      note: `${newOrders} new in 24 hours`,
      icon: ShoppingBag,
      tone: "gold",
    },
    {
      label: "Products",
      value: totalProducts.toLocaleString("en-IN"),
      note: `${healthyStock} healthy stock`,
      icon: Diamond,
      tone: "maroon",
    },
    {
      label: "Avg. Stock",
      value: Number(averageStock || 0).toFixed(1),
      note: `${lowStock} low stock items`,
      icon: Package,
      tone: "gold",
    },
  ];

  const quickActions = [
    {
      label: "Add Product",
      icon: Diamond,
      action: () => navigate("/admin/add-product"),
    },
    {
      label: "View Orders",
      icon: ShoppingBag,
      action: () => navigate("/admin/recent-orders"),
    },
    {
      label: "Add Collection",
      icon: Gift,
      action: () => navigate("/admin/add-category"),
    },
    {
      label: "Generate Report",
      icon: TrendingUp,
      action: () => navigate("/admin/detailed-report"),
    },
    {
      label: "Manage Offers",
      icon: Sparkles,
      action: () => navigate("/admin/banner/coupon"),
    },
  ];

  // Loading State
  if (loading) {
    return (
      <div className="luxury-dashboard-state">
        <div className="luxury-state-card">
          <div className="luxury-loader">
            <Sparkles />
          </div>
          <h2>Preparing your dashboard</h2>
          <p>Loading live sales, product and order information...</p>
        </div>

        <style>{dashboardStyles}</style>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="luxury-dashboard-state">
        <div className="luxury-state-card">
          <div className="luxury-error-icon">!</div>
          <h2>Dashboard could not be loaded</h2>
          <p>{error}</p>
          <button type="button" onClick={fetchDashboardData}>
            Try Again
          </button>
        </div>

        <style>{dashboardStyles}</style>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="luxury-dashboard-page">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <StockUpdateModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        product={selectedProduct}
        onUpdate={handleStockUpdate}
      />

      <section className="luxury-dashboard-shell">
        <header className="luxury-dashboard-header">
          <div className="luxury-welcome-copy">
            <p className="luxury-eyebrow">SL GOLD ADMIN</p>
            <h1>
              Welcome back, <span>{adminName}!</span>
              <Sparkles />
            </h1>
            <p>Here’s what’s happening with your jewellery store today.</p>
          </div>

          <div className="luxury-header-tools">
            <label className="luxury-search">
              <Search />
              <input
                type="search"
                placeholder="Search products by name or ID..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>

            <div className="luxury-icon-actions">
              <button
                type="button"
                aria-label="Refresh dashboard"
                onClick={() => {
                  fetchDashboardData();
                  fetchAllProducts();
                }}
              >
                <RefreshCw />
              </button>

              <button type="button" aria-label="Notifications">
                <Bell />
                {newOrders > 0 && (
                  <span>{Math.min(newOrders, 99)}</span>
                )}
              </button>
            </div>
          </div>
        </header>

        <div className="luxury-date-row">
          <button type="button" className="luxury-date-pill">
            <CalendarDays />
            <span>
              {new Date().toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </button>
        </div>

        <section className="luxury-stat-grid">
          {dashboardStats.map((stat, index) => {
            const Icon = stat.icon;

            return (
              <article
                key={stat.label}
                ref={(element) => {
                  cardsRef.current[index] = element;
                }}
                className="luxury-stat-card"
              >
                <div className={`luxury-stat-icon luxury-stat-${stat.tone}`}>
                  <Icon />
                </div>

                <div>
                  <p>{stat.label}</p>
                  <h2>{stat.value}</h2>
                  <span>
                    <TrendingUp />
                    {stat.note}
                  </span>
                </div>

                <svg
                  className="luxury-stat-sparkline"
                  viewBox="0 0 120 34"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <polyline
                    points="0,27 12,26 24,21 36,23 48,15 60,20 72,13 84,17 96,9 108,11 120,4"
                  />
                </svg>
              </article>
            );
          })}
        </section>

        <section className="luxury-main-grid">
          <article className="luxury-panel luxury-sales-panel">
            <div className="luxury-panel-heading">
              <div>
                <p className="luxury-panel-kicker">Performance</p>
                <h2>Sales Overview</h2>
              </div>

              <button type="button">Last 7 Days</button>
            </div>

            <div className="luxury-chart-legend">
              <span>
                <i className="luxury-legend-this" />
                Revenue
              </span>
              <span>
                <i className="luxury-legend-last" />
                Store trend
              </span>
            </div>

            <div className="luxury-line-chart">
              <div className="luxury-chart-grid" />

              {chartPoints.length > 0 ? (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-label="Sales revenue chart"
                >
                  <defs>
                    <linearGradient
                      id="salesArea"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#5b080d" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#5b080d" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  <polygon
                    points={`0,100 ${chartPolyline} 100,100`}
                    fill="url(#salesArea)"
                  />

                  <polyline
                    points={chartPolyline}
                    fill="none"
                    stroke="#5b080d"
                    strokeWidth="1.8"
                    vectorEffect="non-scaling-stroke"
                  />

                  {chartPoints.map((point, index) => (
                    <circle
                      key={`${point.x}-${index}`}
                      cx={point.x}
                      cy={point.y}
                      r="1.5"
                      fill="#5b080d"
                    />
                  ))}
                </svg>
              ) : (
                <div className="luxury-empty-chart">No sales data yet</div>
              )}
            </div>

            <div className="luxury-chart-labels">
              {salesTrend.map((item, index) => (
                <span key={`${item.label}-${index}`}>{item.label}</span>
              ))}
            </div>
          </article>

          <article className="luxury-panel luxury-top-products">
            <div className="luxury-panel-heading">
              <div>
                <p className="luxury-panel-kicker">Popular</p>
                <h2>Top Products</h2>
              </div>

              <button
                type="button"
                onClick={() => navigate("/admin/product-list")}
              >
                View all
              </button>
            </div>

            <div className="luxury-product-list">
              {bestSellingProducts.slice(0, 5).map((product, index) => {
                const imageUrl = getProductImageUrl(product.img_path);

                return (
                  <div key={product.product_id} className="luxury-product-row">
                    <span className="luxury-product-rank">{index + 1}</span>

                    <div className="luxury-product-image">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={product.name}
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <Diamond />
                      )}
                    </div>

                    <div className="luxury-product-copy">
                      <h3>{product.name}</h3>
                      <p>{formatCurrency(product.revenue)}</p>
                    </div>

                    <div className="luxury-product-orders">
                      <strong>{product.total_sold || 0}</strong>
                      <span>Sold</span>
                    </div>
                  </div>
                );
              })}

              {!bestSellingProducts.length && (
                <p className="luxury-empty-copy">
                  Best-selling products will appear after completed orders.
                </p>
              )}
            </div>
          </article>
        </section>

        <section className="luxury-secondary-grid">
          <article className="luxury-panel luxury-donut-panel">
            <div className="luxury-panel-heading">
              <div>
                <p className="luxury-panel-kicker">Categories</p>
                <h2>Revenue by Category</h2>
              </div>
            </div>

            <div className="luxury-donut-layout">
              <div className="luxury-donut">
                <svg viewBox="0 0 42 42">
                  <circle
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke="#f1e9e1"
                    strokeWidth="7"
                  />

                  {(() => {
                    const total = revenueByCategory.reduce(
                      (sum, item) => sum + Number(item.revenue || 0),
                      0
                    );
                    let offset = 25;
                    const colors = [
                      "#5b080d",
                      "#d59432",
                      "#d9aba6",
                      "#9b776d",
                      "#2b0000",
                    ];

                    return total > 0
                      ? revenueByCategory.slice(0, 5).map((item, index) => {
                          const value = Number(item.revenue || 0);
                          const dash = (value / total) * 100;
                          const circle = (
                            <circle
                              key={`${item.label}-${index}`}
                              cx="21"
                              cy="21"
                              r="15.915"
                              fill="transparent"
                              stroke={colors[index % colors.length]}
                              strokeWidth="7"
                              strokeDasharray={`${dash} ${100 - dash}`}
                              strokeDashoffset={offset}
                            />
                          );
                          offset -= dash;
                          return circle;
                        })
                      : null;
                  })()}
                </svg>

                <div>
                  <span>Total</span>
                  <strong>{formatCurrency(totalSales)}</strong>
                </div>
              </div>

              <div className="luxury-category-list">
                {revenueByCategory.slice(0, 5).map((item, index) => {
                  const total = revenueByCategory.reduce(
                    (sum, category) =>
                      sum + Number(category.revenue || 0),
                    0
                  );
                  const percentage = total
                    ? Math.round(
                        (Number(item.revenue || 0) / total) * 100
                      )
                    : 0;

                  return (
                    <div key={`${item.label}-${index}`}>
                      <span>
                        <i data-index={index} />
                        {item.label}
                      </span>
                      <strong>{percentage}%</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </article>

          <article className="luxury-panel luxury-revenue-bars">
            <div className="luxury-panel-heading">
              <div>
                <p className="luxury-panel-kicker">Growth</p>
                <h2>Revenue Trend</h2>
              </div>
            </div>

            <div className="luxury-bars">
              {salesTrend.slice(-7).map((item, index) => {
                const value = Number(item.revenue || 0);
                const height = Math.max(18, (value / maxTrendValue) * 100);

                return (
                  <div key={`${item.label}-${index}`} className="luxury-bar">
                    <span style={{ height: `${height}%` }}>
                      <i>{formatCurrency(value)}</i>
                    </span>
                    <small>{item.label}</small>
                  </div>
                );
              })}
            </div>
          </article>

          <article className="luxury-insights-stack">
            <div className="luxury-mini-insight">
              <span className="luxury-mini-icon luxury-mini-pink">
                <WalletCards />
              </span>
              <div>
                <p>Total Sales</p>
                <strong>{formatCurrency(totalSales)}</strong>
                <small>Live store revenue</small>
              </div>
            </div>

            <div className="luxury-mini-insight">
              <span className="luxury-mini-icon luxury-mini-gold">
                <Crown />
              </span>
              <div>
                <p>Best Selling</p>
                <strong>
                  {bestSellingProducts[0]?.name || "No sales yet"}
                </strong>
                <small>
                  {bestSellingProducts[0]
                    ? `${bestSellingProducts[0].total_sold} sold`
                    : "Waiting for completed orders"}
                </small>
              </div>
            </div>

            <div className="luxury-mini-insight">
              <span className="luxury-mini-icon luxury-mini-pink">
                <UsersRound />
              </span>
              <div>
                <p>Completed Orders</p>
                <strong>{completedOrders}</strong>
                <small>{pendingOrders} pending actions</small>
              </div>
            </div>
          </article>

          <article className="luxury-panel luxury-recent-orders">
            <div className="luxury-panel-heading">
              <div>
                <p className="luxury-panel-kicker">Latest</p>
                <h2>Recent Orders</h2>
              </div>

              <button
                type="button"
                onClick={() => navigate("/admin/recent-orders")}
              >
                View all
              </button>
            </div>

            <div className="luxury-order-list">
              {recentOrders.slice(0, 5).map((order, index) => (
                <button
                  type="button"
                  key={order.order_id}
                  className="luxury-order-row"
                  onClick={() =>
                    navigate(
                      `/admin/AdminViewDetails/${order.order_id}`
                    )
                  }
                >
                  <span className="luxury-order-avatar">
                    {String(order.full_name || "C")
                      .trim()
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                  <span className="luxury-order-copy">
                    <small>#{order.order_id}</small>
                    <strong>{order.full_name || "Customer"}</strong>
                  </span>

                  <span className="luxury-order-value">
                    {formatCurrency(order.total_amount)}
                  </span>

                  <span
                    className={`luxury-order-status ${getStatusBadge(
                      order.status
                    )}`}
                  >
                    {order.status}
                  </span>
                </button>
              ))}

              {!recentOrders.length && (
                <p className="luxury-empty-copy">No recent orders yet.</p>
              )}
            </div>
          </article>
        </section>

       

        <section className="luxury-stock-section">
          <div className="luxury-section-heading">
            <div>
              <p className="luxury-panel-kicker">Inventory Control</p>
              <h2>Stock Management</h2>
              <span>
                Search, filter and update your product stock without changing
                your backend flow.
              </span>
            </div>

            <button type="button" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>

          <div className="luxury-stock-stats">
            {[
              ["Total Products", totalProducts],
              ["Healthy Stock", healthyStock],
              ["Low Stock", lowStock],
              ["Critical", criticalStock],
              ["Out of Stock", outOfStock],
            ].map(([label, value]) => (
              <article key={label}>
                <p>{label}</p>
                <strong>{value}</strong>
              </article>
            ))}
          </div>

          <div className="luxury-filter-grid">
            <label>
              <span>Show Products</span>
              <select
                value={filterType}
                onChange={(event) => {
                  setFilterType(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="lowest">Lowest Stock</option>
                <option value="critical">Critical Stock (&lt;5)</option>
                <option value="low">Low Stock (&lt;10)</option>
                <option value="out">Out of Stock</option>
                <option value="healthy">Healthy Stock (≥20)</option>
                <option value="all">All Products</option>
              </select>
            </label>

            {filterType !== "all" && (
              <label>
                <span>Limit</span>
                <select
                  value={filterLimit}
                  onChange={(event) =>
                    setFilterLimit(Number(event.target.value))
                  }
                >
                  <option value={10}>10 Products</option>
                  <option value={20}>20 Products</option>
                  <option value={50}>50 Products</option>
                  <option value={100}>100 Products</option>
                </select>
              </label>
            )}

            <label>
              <span>Category</span>
              <select
                value={categoryFilter}
                onChange={(event) => {
                  setCategoryFilter(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Sort By</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
              >
                <option value="stock_asc">Stock: Low to High</option>
                <option value="stock_desc">Stock: High to Low</option>
                <option value="name_asc">Name: A to Z</option>
                <option value="name_desc">Name: Z to A</option>
              </select>
            </label>

            <label>
              <span>Search</span>
              <div className="luxury-filter-search">
                <Search />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Product name or ID"
                />
              </div>
            </label>
          </div>

          {loadingProducts ? (
            <div className="luxury-products-loading">
              <div className="luxury-loader">
                <Sparkles />
              </div>
              <p>Loading products...</p>
            </div>
          ) : (
            <>
              <div className="luxury-stock-table-wrap">
                <table className="luxury-stock-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>ID</th>
                      <th>Category</th>
                      <th>Stock</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(filterType === "all"
                      ? paginatedProducts
                      : filteredProducts
                    ).map((product) => {
                      const imageUrl = getProductImageUrl(product.img_path);

                      return (
                        <tr key={product.product_id}>
                          <td>
                            <div className="luxury-table-product">
                              <span>
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={product.name}
                                    onError={(event) => {
                                      event.currentTarget.style.display =
                                        "none";
                                    }}
                                  />
                                ) : (
                                  <Diamond />
                                )}
                              </span>

                              <div>
                                <strong>{product.name}</strong>
                                <small>{product.stock_message}</small>
                              </div>
                            </div>
                          </td>

                          <td>#{product.product_id}</td>
                          <td>{product.category_name || "Uncategorized"}</td>

                          <td>
                            <strong className="luxury-stock-number">
                              {product.stock_quantity}
                            </strong>
                          </td>

                          <td>
                            <span
                              className={`luxury-stock-badge luxury-stock-${getStockStatus(
                                product.stock_quantity
                              )}`}
                            >
                              {getStockMessage(product.stock_quantity)}
                            </span>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="luxury-update-stock"
                              onClick={() => openStockModal(product)}
                            >
                              Update
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {!filteredProducts.length && (
                  <div className="luxury-empty-table">
                    No products match these filters.
                  </div>
                )}
              </div>

              {filterType === "all" && totalPages > 1 && (
                <div className="luxury-pagination">
                  <p>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredProducts.length
                    )}{" "}
                    of {filteredProducts.length}
                  </p>

                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((page) => Math.max(1, page - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>

                    <span>
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((page) =>
                          Math.min(totalPages, page + 1)
                        )
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </section>

      <nav className="luxury-mobile-nav">
        {[
          ["Dashboard", FaChartBar, "/admin/dashboard"],
          ["Orders", ShoppingBag, "/admin/recent-orders"],
          ["Products", Diamond, "/admin/product-list"],
          ["Customers", UserRound, "/admin/user-list"],
          ["More", Package, "/admin/banner"],
        ].map(([label, Icon, path], index) => (
          <button
            type="button"
            key={label}
            onClick={() => navigate(path)}
            className={index === 0 ? "luxury-mobile-active" : ""}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      <style>{dashboardStyles}</style>
    </div>
  );
}

const dashboardStyles = `
  .luxury-dashboard-page,
  .luxury-dashboard-page *,
  .luxury-dashboard-state,
  .luxury-dashboard-state * {
    box-sizing: border-box;
  }

  .luxury-dashboard-page {
    --dash-maroon: #2b0000;
    --dash-deep: #170000;
    --dash-red: #5b080d;
    --dash-gold: #d59432;
    --dash-gold-soft: #edc172;
    --dash-cream: #fffaf4;
    --dash-paper: #fbf5ef;
    --dash-line: #eadfd6;
    --dash-text: #2f1715;
    --dash-muted: #8d817a;

    min-height: 100vh;
    overflow-x: hidden;
    padding: 20px 18px 34px;
    color: var(--dash-text);
    background:
      radial-gradient(circle at 50% -8%, rgba(213, 148, 50, 0.08), transparent 28%),
      linear-gradient(135deg, #f8efe8 0%, #fffaf5 55%, #f7eee7 100%);
    font-family: "Kanit", Arial, sans-serif;
  }

  .luxury-dashboard-shell {
    width: 100%;
    margin: 0;
    padding: 0;
  }

  .luxury-dashboard-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 620px);
    align-items: center;
    gap: 34px;
  }

  .luxury-eyebrow,
  .luxury-panel-kicker {
    margin: 0 0 7px;
    color: var(--dash-gold);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
  }

  .luxury-welcome-copy h1 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--dash-maroon);
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(30px, 3vw, 45px);
    font-weight: 500;
    line-height: 1.08;
  }

  .luxury-welcome-copy h1 span {
    color: #4b0a0e;
  }

  .luxury-welcome-copy h1 svg {
    width: 22px;
    height: 22px;
    color: var(--dash-gold);
  }

  .luxury-welcome-copy > p:last-child {
    margin: 7px 0 0;
    color: var(--dash-muted);
    font-size: 14px;
  }

  .luxury-header-tools {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .luxury-search,
  .luxury-filter-search {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
    border: 1px solid var(--dash-line);
    border-radius: 16px;
    background: rgba(255, 252, 248, 0.82);
    box-shadow: 0 10px 28px rgba(56, 21, 17, 0.055);
  }

  .luxury-search {
    min-height: 52px;
    flex: 1;
    padding: 0 17px;
  }

  .luxury-search svg,
  .luxury-filter-search svg {
    width: 19px;
    height: 19px;
    flex: none;
    color: #806e66;
  }

  .luxury-search input,
  .luxury-filter-search input {
    width: 100%;
    border: 0;
    outline: 0;
    color: var(--dash-text);
    background: transparent;
    font: inherit;
  }

  .luxury-icon-actions {
    display: flex;
    gap: 10px;
  }

  .luxury-icon-actions button {
    position: relative;
    display: grid;
    width: 51px;
    height: 51px;
    place-items: center;
    border: 1px solid var(--dash-line);
    border-radius: 15px;
    color: var(--dash-maroon);
    background: rgba(255, 252, 248, 0.8);
    box-shadow: 0 9px 25px rgba(56, 21, 17, 0.05);
    cursor: pointer;
    transition: transform 220ms ease, background 220ms ease;
  }

  .luxury-icon-actions button:hover {
    color: white;
    background: var(--dash-maroon);
    transform: translateY(-3px);
  }

  .luxury-icon-actions button svg {
    width: 20px;
    height: 20px;
  }

  .luxury-icon-actions button span {
    position: absolute;
    top: -5px;
    right: -4px;
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    border-radius: 999px;
    color: white;
    background: var(--dash-red);
    font-size: 10px;
    line-height: 19px;
  }

  .luxury-date-row {
    display: flex;
    justify-content: flex-end;
    margin: 10px 0 13px;
  }

  .luxury-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
    padding: 8px 15px;
    border: 1px solid var(--dash-line);
    border-radius: 13px;
    color: var(--dash-text);
    background: rgba(255, 252, 248, 0.78);
    font-family: inherit;
  }

  .luxury-date-pill svg {
    width: 17px;
    height: 17px;
    color: var(--dash-gold);
  }

  .luxury-stat-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px;
  }

  .luxury-dashboard-page > .luxury-dashboard-shell > .luxury-stock-section {
    display: none;
  }

  .luxury-stat-card,
  .luxury-panel,
  .luxury-mini-insight,
  .luxury-stock-section {
    border: 1px solid var(--dash-line);
    background:
      radial-gradient(circle at 90% 0%, rgba(213, 148, 50, 0.05), transparent 34%),
      rgba(255, 252, 248, 0.9);
    box-shadow: 0 14px 35px rgba(56, 21, 17, 0.055);
  }

  .luxury-stat-card {
    position: relative;
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 14px;
    min-height: 142px;
    padding: 20px;
    overflow: hidden;
    border-radius: 17px;
  }

  .luxury-stat-icon {
    display: grid;
    width: 54px;
    height: 54px;
    place-items: center;
    border-radius: 50%;
    color: white;
  }

  .luxury-stat-maroon {
    background: linear-gradient(145deg, #6f1218, #2b0000);
  }

  .luxury-stat-gold {
    background: linear-gradient(145deg, #e2aa4e, #b66e0e);
  }

  .luxury-stat-icon svg {
    width: 26px;
    height: 26px;
  }

  .luxury-stat-card p {
    margin: 2px 0 5px;
    color: #6d5e57;
    font-size: 12px;
  }

  .luxury-stat-card h2 {
    margin: 0;
    color: var(--dash-text);
    font-size: clamp(22px, 2vw, 29px);
    font-weight: 500;
    line-height: 1.1;
  }

  .luxury-stat-card span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 9px;
    color: #3a9852;
    font-size: 10px;
  }

  .luxury-stat-card span svg {
    width: 12px;
    height: 12px;
  }

  .luxury-stat-sparkline {
    position: absolute;
    right: 17px;
    bottom: 10px;
    width: 46%;
    height: 35px;
    opacity: 0.9;
  }

  .luxury-stat-sparkline polyline {
    fill: none;
    stroke: var(--dash-gold);
    stroke-width: 1.6;
    vector-effect: non-scaling-stroke;
  }

  .luxury-main-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.65fr) minmax(330px, 0.95fr);
    gap: 18px;
    margin-top: 18px;
  }

  .luxury-panel {
    border-radius: 18px;
    padding: 20px;
  }

  .luxury-panel-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  }

  .luxury-panel-heading h2,
  .luxury-section-heading h2 {
    margin: 0;
    color: var(--dash-text);
    font-size: 17px;
    font-weight: 600;
  }

  .luxury-panel-heading button,
  .luxury-section-heading button {
    border: 0;
    color: var(--dash-red);
    background: transparent;
    font-family: inherit;
    font-size: 11px;
    cursor: pointer;
  }

  .luxury-chart-legend {
    display: flex;
    gap: 24px;
    margin-top: 15px;
    color: #6e6059;
    font-size: 10px;
  }

  .luxury-chart-legend span {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .luxury-chart-legend i {
    width: 22px;
    height: 2px;
  }

  .luxury-legend-this {
    background: var(--dash-red);
  }

  .luxury-legend-last {
    border-top: 1px dashed var(--dash-gold);
  }

  .luxury-line-chart {
    position: relative;
    height: 235px;
    margin-top: 17px;
  }

  .luxury-chart-grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(to bottom, rgba(70, 43, 37, 0.1) 1px, transparent 1px);
    background-size: 100% 25%;
  }

  .luxury-line-chart svg {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .luxury-chart-labels {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    color: #776a64;
    font-size: 9px;
  }

  .luxury-empty-chart,
  .luxury-empty-copy {
    display: grid;
    min-height: 130px;
    place-items: center;
    color: var(--dash-muted);
    font-size: 13px;
    text-align: center;
  }

  .luxury-product-list,
  .luxury-order-list {
    margin-top: 10px;
  }

  .luxury-product-row {
    display: grid;
    grid-template-columns: 30px 58px minmax(0, 1fr) auto;
    align-items: center;
    gap: 11px;
    padding: 10px 0;
    border-bottom: 1px solid #eee4dc;
  }

  .luxury-product-row:last-child {
    border-bottom: 0;
  }

  .luxury-product-rank {
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    border-radius: 7px;
    color: #7e6a60;
    background: #f4ece6;
    font-size: 10px;
  }

  .luxury-product-image {
    display: grid;
    width: 56px;
    height: 48px;
    place-items: center;
    overflow: hidden;
    border-radius: 10px;
    background: #f7efe7;
  }

  .luxury-product-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .luxury-product-image svg {
    width: 24px;
    color: var(--dash-gold);
  }

  .luxury-product-copy {
    min-width: 0;
  }

  .luxury-product-copy h3 {
    overflow: hidden;
    margin: 0;
    color: var(--dash-text);
    font-family: Georgia, "Times New Roman", serif;
    font-size: 12px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .luxury-product-copy p {
    margin: 4px 0 0;
    font-size: 11px;
    font-weight: 600;
  }

  .luxury-product-orders {
    text-align: right;
  }

  .luxury-product-orders strong,
  .luxury-product-orders span {
    display: block;
  }

  .luxury-product-orders strong {
    font-size: 12px;
  }

  .luxury-product-orders span {
    color: var(--dash-muted);
    font-size: 9px;
  }

  .luxury-secondary-grid {
    display: grid;
    grid-template-columns: 1.15fr 1.15fr 0.72fr 1.45fr;
    gap: 18px;
    margin-top: 18px;
  }

  .luxury-donut-layout {
    display: flex;
    align-items: center;
    gap: 22px;
    margin-top: 13px;
  }

  .luxury-donut {
    position: relative;
    width: 175px;
    height: 175px;
    flex: none;
  }

  .luxury-donut svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  .luxury-donut > div {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    text-align: center;
  }

  .luxury-donut span {
    color: var(--dash-muted);
    font-size: 10px;
  }

  .luxury-donut strong {
    display: block;
    margin-top: 4px;
    font-size: 14px;
  }

  .luxury-category-list {
    min-width: 0;
    flex: 1;
  }

  .luxury-category-list div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    color: #5f5049;
    font-size: 10px;
  }

  .luxury-category-list span {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
  }

  .luxury-category-list i {
    width: 8px;
    height: 8px;
    flex: none;
    border-radius: 50%;
    background: #5b080d;
  }

  .luxury-category-list i[data-index="1"] { background: #d59432; }
  .luxury-category-list i[data-index="2"] { background: #d9aba6; }
  .luxury-category-list i[data-index="3"] { background: #9b776d; }
  .luxury-category-list i[data-index="4"] { background: #2b0000; }

  .luxury-bars {
    display: flex;
    height: 202px;
    align-items: end;
    justify-content: space-around;
    gap: 10px;
    margin-top: 18px;
  }

  .luxury-bar {
    display: flex;
    height: 100%;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: end;
    gap: 8px;
  }

  .luxury-bar > span {
    position: relative;
    width: min(30px, 70%);
    min-height: 18px;
    border-radius: 6px 6px 2px 2px;
    background: linear-gradient(180deg, #f0c57c, #c67a17);
    box-shadow: inset 0 0 8px rgba(255, 255, 255, 0.22);
  }

  .luxury-bar i {
    position: absolute;
    top: -22px;
    left: 50%;
    display: none;
    padding: 3px 6px;
    border-radius: 5px;
    color: white;
    background: var(--dash-maroon);
    font-size: 7px;
    font-style: normal;
    white-space: nowrap;
    transform: translateX(-50%);
  }

  .luxury-bar:hover i {
    display: block;
  }

  .luxury-bar small {
    color: #786b64;
    font-size: 9px;
  }

  .luxury-insights-stack {
    display: grid;
    gap: 10px;
  }

  .luxury-mini-insight {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 94px;
    padding: 14px;
    border-radius: 15px;
  }

  .luxury-mini-icon {
    display: grid;
    width: 45px;
    height: 45px;
    flex: none;
    place-items: center;
    border-radius: 50%;
  }

  .luxury-mini-pink {
    color: var(--dash-red);
    background: #f8dfdd;
  }

  .luxury-mini-gold {
    color: #b96b0d;
    background: #fff0d7;
  }

  .luxury-mini-icon svg {
    width: 22px;
    height: 22px;
  }

  .luxury-mini-insight p {
    margin: 0;
    color: #75665f;
    font-size: 9px;
  }

  .luxury-mini-insight strong {
    display: block;
    overflow: hidden;
    max-width: 160px;
    margin-top: 3px;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .luxury-mini-insight small {
    display: block;
    margin-top: 4px;
    color: #4b9b5d;
    font-size: 8px;
  }

  .luxury-order-row {
    display: grid;
    width: 100%;
    grid-template-columns: 38px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 11px;
    padding: 9px 0;
    border: 0;
    border-bottom: 1px solid #eee4dc;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
  }

  .luxury-order-row:last-child {
    border-bottom: 0;
  }

  .luxury-order-avatar {
    display: grid;
    width: 35px;
    height: 35px;
    place-items: center;
    border-radius: 50%;
    color: white;
    background: linear-gradient(145deg, #d59432, #5b080d);
    font-family: Georgia, serif;
  }

  .luxury-order-copy small,
  .luxury-order-copy strong {
    display: block;
  }

  .luxury-order-copy small {
    color: var(--dash-muted);
    font-size: 8px;
  }

  .luxury-order-copy strong {
    margin-top: 2px;
    font-size: 10px;
  }

  .luxury-order-value {
    font-size: 10px;
    font-weight: 600;
  }

  .luxury-order-status {
    min-width: 70px;
    padding: 5px 8px;
    border: 1px solid;
    border-radius: 8px;
    font-size: 8px;
    text-align: center;
    text-transform: capitalize;
  }

  .luxury-business-row {
    display: grid;
    grid-template-columns: 1.45fr 1fr;
    gap: 18px;
    margin-top: 18px;
  }

  .luxury-business-banner {
    position: relative;
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr) auto;
    align-items: center;
    gap: 18px;
    min-height: 135px;
    padding: 24px;
    overflow: hidden;
    border: 1px solid rgba(213, 148, 50, 0.35);
    border-radius: 18px;
    color: #fff7e8;
    background:
      radial-gradient(circle at 90% 20%, rgba(153, 16, 22, 0.52), transparent 28%),
      linear-gradient(135deg, #4d070b, #210000);
    box-shadow: 0 18px 40px rgba(43, 0, 0, 0.18);
  }

  .luxury-business-icon {
    display: grid;
    width: 54px;
    height: 54px;
    place-items: center;
    border: 1px solid rgba(237, 193, 114, 0.48);
    border-radius: 50%;
    color: var(--dash-gold-soft);
  }

  .luxury-business-icon svg {
    width: 25px;
  }

  .luxury-business-banner p {
    margin: 0 0 5px;
    color: var(--dash-gold-soft);
    font-size: 13px;
  }

  .luxury-business-banner h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
  }

  .luxury-business-banner span {
    display: block;
    margin-top: 6px;
    color: rgba(255, 247, 232, 0.65);
    font-size: 9px;
  }

  .luxury-business-banner button {
    position: relative;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 42px;
    padding: 10px 15px;
    border: 1px solid var(--dash-gold);
    border-radius: 9px;
    color: var(--dash-gold-soft);
    background: rgba(23, 0, 0, 0.45);
    font-family: inherit;
    cursor: pointer;
  }

  .luxury-business-banner button svg {
    width: 15px;
  }

  .luxury-banner-jewel {
    position: absolute;
    right: 14%;
    bottom: -35px;
    opacity: 0.13;
  }

  .luxury-banner-jewel svg {
    width: 150px;
    height: 150px;
    color: var(--dash-gold-soft);
  }

  .luxury-action-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 15px;
  }

  .luxury-action-grid button {
    display: flex;
    min-height: 77px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px;
    border: 1px solid var(--dash-line);
    border-radius: 12px;
    color: var(--dash-red);
    background: rgba(255, 252, 248, 0.7);
    font-family: inherit;
    cursor: pointer;
    transition: transform 220ms ease, background 220ms ease;
  }

  .luxury-action-grid button:hover {
    color: white;
    background: var(--dash-maroon);
    transform: translateY(-4px);
  }

  .luxury-action-grid button svg {
    width: 22px;
    height: 22px;
  }

  .luxury-action-grid button span {
    color: inherit;
    font-size: 8px;
    text-align: center;
  }

  .luxury-stock-section {
    margin-top: 18px;
    padding: 22px;
    border-radius: 18px;
  }

  .luxury-section-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
  }

  .luxury-section-heading > div > span {
    display: block;
    margin-top: 6px;
    color: var(--dash-muted);
    font-size: 11px;
  }

  .luxury-stock-stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 18px;
  }

  .luxury-stock-stats article {
    padding: 14px;
    border: 1px solid var(--dash-line);
    border-radius: 13px;
    background: #fffaf5;
  }

  .luxury-stock-stats p {
    margin: 0;
    color: #786961;
    font-size: 9px;
  }

  .luxury-stock-stats strong {
    display: block;
    margin-top: 5px;
    color: var(--dash-maroon);
    font-size: 22px;
  }

  .luxury-filter-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 18px;
  }

  .luxury-filter-grid label > span {
    display: block;
    margin-bottom: 6px;
    color: #75665f;
    font-size: 9px;
  }

  .luxury-filter-grid select,
  .luxury-filter-search {
    width: 100%;
    min-height: 42px;
    border: 1px solid var(--dash-line);
    border-radius: 11px;
    outline: 0;
    color: var(--dash-text);
    background: #fffaf5;
    font: inherit;
    font-size: 11px;
  }

  .luxury-filter-grid select {
    padding: 0 11px;
  }

  .luxury-filter-search {
    padding: 0 11px;
  }

  .luxury-stock-table-wrap {
    margin-top: 18px;
    overflow-x: auto;
    border: 1px solid var(--dash-line);
    border-radius: 14px;
    background: #fffaf5;
  }

  .luxury-stock-table {
    width: 100%;
    min-width: 800px;
    border-collapse: collapse;
  }

  .luxury-stock-table th,
  .luxury-stock-table td {
    padding: 13px 15px;
    border-bottom: 1px solid #eee4dc;
    text-align: left;
    white-space: nowrap;
  }

  .luxury-stock-table th {
    color: #786961;
    background: #f7eee8;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .luxury-stock-table td {
    color: #5c4d47;
    font-size: 11px;
  }

  .luxury-table-product {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .luxury-table-product > span {
    display: grid;
    width: 43px;
    height: 43px;
    flex: none;
    place-items: center;
    overflow: hidden;
    border-radius: 10px;
    background: #f6ece3;
  }

  .luxury-table-product img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .luxury-table-product svg {
    width: 20px;
    color: var(--dash-gold);
  }

  .luxury-table-product strong,
  .luxury-table-product small {
    display: block;
  }

  .luxury-table-product strong {
    max-width: 250px;
    overflow: hidden;
    color: var(--dash-text);
    font-size: 11px;
    text-overflow: ellipsis;
  }

  .luxury-table-product small {
    margin-top: 3px;
    color: var(--dash-muted);
    font-size: 8px;
  }

  .luxury-stock-number {
    color: var(--dash-maroon);
    font-size: 16px;
  }

  .luxury-stock-badge {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
    font-size: 8px;
  }

  .luxury-stock-good {
    color: #347b45;
    background: #eaf4e8;
  }

  .luxury-stock-warning {
    color: #a66a14;
    background: #fff2d8;
  }

  .luxury-stock-critical {
    color: #a22e2b;
    background: #fce4e2;
  }

  .luxury-stock-out {
    color: #6f6560;
    background: #eeeae7;
  }

  .luxury-update-stock {
    min-height: 32px;
    padding: 6px 13px;
    border: 0;
    border-radius: 8px;
    color: white;
    background: var(--dash-maroon);
    font: inherit;
    font-size: 9px;
    cursor: pointer;
  }

  .luxury-empty-table,
  .luxury-products-loading {
    padding: 35px;
    color: var(--dash-muted);
    text-align: center;
  }

  .luxury-products-loading {
    display: grid;
    place-items: center;
    gap: 8px;
  }

  .luxury-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-top: 14px;
    color: #766860;
    font-size: 10px;
  }

  .luxury-pagination p {
    margin: 0;
  }

  .luxury-pagination > div {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .luxury-pagination button {
    min-height: 34px;
    padding: 6px 11px;
    border: 1px solid var(--dash-line);
    border-radius: 8px;
    color: var(--dash-maroon);
    background: white;
    font: inherit;
    font-size: 9px;
  }

  .luxury-pagination button:disabled {
    opacity: 0.4;
  }

  .luxury-mobile-nav {
    display: none;
  }

  .luxury-dashboard-state {
    display: grid;
    min-height: 100vh;
    place-items: center;
    padding: 25px;
    background: linear-gradient(135deg, #f8efe8, #fffaf5);
    font-family: "Kanit", Arial, sans-serif;
  }

  .luxury-state-card {
    width: min(470px, 100%);
    padding: 40px;
    border: 1px solid #eadfd6;
    border-radius: 20px;
    background: #fffaf5;
    box-shadow: 0 22px 55px rgba(43, 0, 0, 0.1);
    text-align: center;
  }

  .luxury-loader {
    display: grid;
    width: 60px;
    height: 60px;
    margin: 0 auto;
    place-items: center;
    border: 2px solid #edc172;
    border-radius: 50%;
    color: #d59432;
    animation: luxurySpin 1.4s linear infinite;
  }

  .luxury-loader svg {
    width: 25px;
    height: 25px;
  }

  .luxury-state-card h2 {
    margin: 20px 0 8px;
    color: #2b0000;
    font-family: Georgia, serif;
    font-size: 27px;
  }

  .luxury-state-card p {
    margin: 0;
    color: #8d817a;
    font-size: 13px;
  }

  .luxury-state-card button {
    margin-top: 20px;
    padding: 10px 18px;
    border: 0;
    border-radius: 9px;
    color: white;
    background: #2b0000;
  }

  .luxury-error-icon {
    display: grid;
    width: 60px;
    height: 60px;
    margin: 0 auto;
    place-items: center;
    border-radius: 50%;
    color: white;
    background: #922f2b;
    font-size: 28px;
    font-weight: 700;
  }

  @keyframes luxurySpin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 1280px) {
    .luxury-secondary-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .luxury-insights-stack {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .luxury-recent-orders {
      grid-column: span 2;
    }
  }

  @media (max-width: 1024px) {
    .luxury-dashboard-header {
      grid-template-columns: 1fr;
    }

    .luxury-date-row {
      justify-content: flex-start;
    }

    .luxury-stat-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .luxury-main-grid,
    .luxury-business-row {
      grid-template-columns: 1fr;
    }

    .luxury-filter-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 700px) {
    .luxury-dashboard-page {
      padding: 0 0 76px;
    }

    .luxury-dashboard-shell {
      padding: 18px 12px 24px;
    }

    .luxury-dashboard-header {
      display: block;
    }

    .luxury-welcome-copy {
      position: relative;
      min-height: 128px;
      padding: 22px 105px 20px 18px;
      border: 1px solid var(--dash-line);
      border-radius: 18px;
      background:
        radial-gradient(circle at 90% 20%, rgba(213, 148, 50, 0.12), transparent 30%),
        #fffaf5;
      box-shadow: 0 14px 35px rgba(56, 21, 17, 0.06);
    }

    .luxury-welcome-copy::after {
      content: "SL";
      position: absolute;
      top: 20px;
      right: 18px;
      display: grid;
      width: 78px;
      height: 90px;
      place-items: center;
      border: 1px solid rgba(213, 148, 50, 0.32);
      border-radius: 40px 40px 12px 12px;
      color: var(--dash-gold);
      background: linear-gradient(180deg, #fff9ef, #f4e8d9);
      font-family: Georgia, serif;
      font-size: 30px;
    }

    .luxury-eyebrow {
      display: none;
    }

    .luxury-welcome-copy h1 {
      display: block;
      font-size: 25px;
    }

    .luxury-welcome-copy h1 svg {
      display: inline;
      width: 18px;
      height: 18px;
      vertical-align: middle;
    }

    .luxury-welcome-copy > p:last-child {
      max-width: 230px;
      font-size: 10px;
      line-height: 1.5;
    }

    .luxury-header-tools {
      display: none;
    }

    .luxury-date-row {
      margin: 10px 0 14px;
    }

    .luxury-date-pill {
      width: 100%;
      justify-content: space-between;
      min-height: 46px;
    }

    .luxury-stat-grid {
      gap: 10px;
    }

    .luxury-stat-card {
      display: block;
      min-height: 180px;
      padding: 15px;
    }

    .luxury-stat-icon {
      width: 43px;
      height: 43px;
      margin-bottom: 15px;
    }

    .luxury-stat-icon svg {
      width: 21px;
      height: 21px;
    }

    .luxury-stat-card h2 {
      font-size: 20px;
    }

    .luxury-stat-sparkline {
      right: 12px;
      bottom: 10px;
      left: 12px;
      width: auto;
    }

    .luxury-main-grid,
    .luxury-secondary-grid,
    .luxury-business-row {
      display: block;
    }

    .luxury-panel,
    .luxury-business-banner,
    .luxury-stock-section {
      margin-top: 12px;
      padding: 15px;
      border-radius: 16px;
    }

    .luxury-main-grid {
      margin-top: 0;
    }

    .luxury-line-chart {
      height: 230px;
    }

    .luxury-top-products {
      margin-top: 12px;
    }

    .luxury-product-row {
      grid-template-columns: 25px 63px minmax(0, 1fr) auto;
    }

    .luxury-product-image {
      width: 61px;
      height: 58px;
    }

    .luxury-donut-layout {
      display: block;
    }

    .luxury-donut {
      width: 200px;
      height: 200px;
      margin: 0 auto;
    }

    .luxury-category-list {
      margin-top: 15px;
    }

    .luxury-revenue-bars {
      margin-top: 12px;
    }

    .luxury-insights-stack {
      display: block;
    }

    .luxury-mini-insight {
      margin-top: 10px;
    }

    .luxury-recent-orders {
      margin-top: 12px;
    }

    .luxury-order-row {
      grid-template-columns: 38px minmax(0, 1fr) auto;
      padding: 12px 0;
    }

    .luxury-order-status {
      grid-column: 3;
      min-width: 64px;
    }

    .luxury-order-value {
      grid-column: 2;
      margin-top: -5px;
    }

    .luxury-business-banner {
      grid-template-columns: 44px minmax(0, 1fr);
      min-height: 180px;
    }

    .luxury-business-icon {
      width: 42px;
      height: 42px;
    }

    .luxury-business-banner button {
      grid-column: 2;
      justify-self: start;
    }

    .luxury-banner-jewel {
      right: -20px;
    }

    .luxury-action-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .luxury-action-grid button:last-child {
      grid-column: span 2;
      width: 50%;
      justify-self: center;
    }

    .luxury-stock-section {
      margin-top: 12px;
    }

    .luxury-section-heading {
      display: block;
    }

    .luxury-section-heading button {
      margin-top: 10px;
    }

    .luxury-stock-stats {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .luxury-stock-stats article:last-child {
      grid-column: span 2;
    }

    .luxury-filter-grid {
      grid-template-columns: 1fr;
    }

    .luxury-stock-table-wrap {
      overflow: visible;
      border: 0;
      background: transparent;
    }

    .luxury-stock-table {
      min-width: 0;
    }

    .luxury-stock-table thead {
      display: none;
    }

    .luxury-stock-table tbody,
    .luxury-stock-table tr,
    .luxury-stock-table td {
      display: block;
      width: 100%;
    }

    .luxury-stock-table tr {
      position: relative;
      margin-bottom: 10px;
      padding: 14px;
      border: 1px solid var(--dash-line);
      border-radius: 13px;
      background: #fffaf5;
    }

    .luxury-stock-table td {
      padding: 4px 0;
      border: 0;
    }

    .luxury-stock-table td:nth-child(2),
    .luxury-stock-table td:nth-child(3) {
      display: inline-block;
      width: auto;
      margin-right: 12px;
      color: var(--dash-muted);
      font-size: 9px;
    }

    .luxury-stock-table td:nth-child(4),
    .luxury-stock-table td:nth-child(5),
    .luxury-stock-table td:nth-child(6) {
      display: inline-block;
      width: auto;
      margin-top: 8px;
      margin-right: 8px;
    }

    .luxury-pagination {
      display: block;
    }

    .luxury-pagination > div {
      margin-top: 10px;
    }

    .luxury-mobile-nav {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 100;
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      min-height: 66px;
      padding: 5px 7px max(5px, env(safe-area-inset-bottom));
      border-top: 1px solid var(--dash-line);
      background: rgba(255, 250, 245, 0.96);
      box-shadow: 0 -10px 25px rgba(43, 0, 0, 0.08);
      backdrop-filter: blur(16px);
    }

    .luxury-mobile-nav button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      border: 0;
      border-radius: 10px;
      color: #715f57;
      background: transparent;
      font-family: inherit;
      font-size: 8px;
    }

    .luxury-mobile-nav button svg {
      width: 18px;
      height: 18px;
    }

    .luxury-mobile-nav .luxury-mobile-active {
      color: white;
      background: linear-gradient(145deg, #e7b45e, #c67a17);
    }
  }

  @media (max-width: 390px) {
    .luxury-stat-card {
      min-height: 170px;
    }

    .luxury-welcome-copy {
      padding-right: 90px;
    }

    .luxury-welcome-copy::after {
      right: 12px;
      width: 66px;
      height: 82px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .luxury-loader {
      animation: none;
    }
  }
`;

export default Dashboard;
